import axiosInstance from "@/lib/axiosInstance";

export const FilesHubQueryKeys = {
  browse: "filesHubBrowse",
  entities: "filesHubEntities",
  spDocuments: "filesHubSpDocuments",
} as const;

export type FilesHubBrowseKind = "clients" | "work-orders" | "service-providers" | "suppliers";

export interface FilesHubFolder {
  id: string;
  name: string;
  subtitle: string | null;
  /**
   * Owning entity (tenant) of the resource. Clients and work orders carry one;
   * service providers and suppliers are platform-global rows with no owner
   * tenant, so both fields come back null for those kinds.
   */
  entityId: string | null;
  entityName: string | null;
  fileCount: number;
}

export interface FilesHubBrowseResult {
  total: number;
  items: FilesHubFolder[];
}

export interface FilesHubBrowseOptions {
  page?: number;
  limit?: number;
  search?: string;
  /** Restrict to one entity's resources. Ignored for kinds with no owner tenant. */
  entityId?: string;
}

export const browseFilesHub = async (
  kind: FilesHubBrowseKind,
  options: FilesHubBrowseOptions,
  signal?: AbortSignal,
): Promise<FilesHubBrowseResult> => {
  const params = new URLSearchParams();
  params.append("page", String(options.page ?? 1));
  params.append("limit", String(options.limit ?? 50));
  if (options.search) params.append("search", options.search);
  if (options.entityId) params.append("entityId", options.entityId);
  const response = await axiosInstance.get(`/files/browse/${kind}?${params}`, { signal });
  return response.data.data;
};

/** An entity folder: the level the client and work order folders nest under. */
export interface FilesHubEntity {
  id: string;
  name: string;
  subtitle: string | null;
  workOrderCount: number;
  clientCount: number;
}

export interface FilesHubEntitiesResult {
  total: number;
  items: FilesHubEntity[];
}

export const browseFilesHubEntities = async (
  options: FilesHubBrowseOptions,
  signal?: AbortSignal,
): Promise<FilesHubEntitiesResult> => {
  const params = new URLSearchParams();
  params.append("page", String(options.page ?? 1));
  params.append("limit", String(options.limit ?? 50));
  if (options.search) params.append("search", options.search);
  const response = await axiosInstance.get(`/files/browse/entities?${params}`, { signal });
  return response.data.data;
};

export interface SpHubDocument {
  id: string;
  name: string;
  documentType: string | null;
  createdAt: string;
  downloadUrl: string;
}

export interface SpHubDocumentsResult {
  provider: { id: string; name: string };
  items: SpHubDocument[];
}

export const getServiceProviderHubDocuments = async (
  serviceProviderId: string,
  signal?: AbortSignal,
): Promise<SpHubDocumentsResult> => {
  const response = await axiosInstance.get(
    `/files/service-provider/${serviceProviderId}/documents`,
    { signal },
  );
  return response.data.data;
};
